import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

import type {
    DeclarationStatus,
    Language,
    LocalizedText,
    PrincipleGroup,
    Scope,
    VerificationLevel,
} from '@/types'

const STORAGE_KEY = 'fip-dashboard.language'

type Dictionary = Record<string, LocalizedText>

const strings: Dictionary = {
    'app.title': { en: 'FIP Dashboard', cs: 'Dashboard FIP' },
    'app.owner': { en: 'NRP · EOSC CZ', cs: 'NRP · EOSC CZ' },
    'app.subtitle': {
        en: 'FAIR enabling resources and FAIR Implementation Profiles of the Czech National Data Infrastructure',
        cs: 'Zdroje umožňující FAIR a profily implementace FAIR Národní datové infrastruktury ČR',
    },
    'footer.funding': {
        en: 'Co-funded by the European Union · Ministry of Education, Youth and Sports',
        cs: 'Spolufinancováno Evropskou unií · Ministerstvo školství, mládeže a tělovýchovy',
    },
    'nav.overview': { en: 'Overview', cs: 'Přehled' },
    'nav.resources': { en: 'Resources', cs: 'Zdroje' },
    'nav.profiles': { en: 'Profiles', cs: 'Profily' },
    'nav.matrix': { en: 'FAIR Matrix', cs: 'Matice FAIR' },
    'nav.glossary': { en: 'Glossary', cs: 'Slovníček' },
    'nav.about': { en: 'About', cs: 'O aplikaci' },

    'common.loading': { en: 'Loading…', cs: 'Načítání…' },
    'common.error': { en: 'Could not load data', cs: 'Data se nepodařilo načíst' },
    'common.errorHint': {
        en: 'Is the backend running on port 8000?',
        cs: 'Běží backend na portu 8000?',
    },
    'common.retry': { en: 'Try again', cs: 'Zkusit znovu' },
    'common.search': { en: 'Search', cs: 'Hledat' },
    'common.searchPlaceholder': {
        en: 'Search resources, standards, services…',
        cs: 'Hledat zdroje, standardy, služby…',
    },
    'common.filters': { en: 'Filters', cs: 'Filtry' },
    'common.clear': { en: 'Clear filters', cs: 'Zrušit filtry' },
    'common.noResults': { en: 'Nothing matches these filters.', cs: 'Filtrům nic neodpovídá.' },
    'common.website': { en: 'Website', cs: 'Webová stránka' },
    'common.back': { en: 'Back', cs: 'Zpět' },
    'common.notFound': { en: 'Not found', cs: 'Nenalezeno' },
    'common.demoData': { en: 'Demonstration data', cs: 'Demonstrační data' },
    'common.demoBadge': { en: 'Demo', cs: 'Ukázka' },
    'common.demoNotice': {
        en: 'This dashboard contains example data only and currently serves for demonstration and feedback purposes.',
        cs: 'Tento dashboard obsahuje pouze ukázková data a v současnosti slouží k demonstraci a sběru zpětné vazby.',
    },

    'overview.heroTitle': {
        en: 'FAIR enabling resources of the Czech National Data Infrastructure',
        cs: 'Zdroje umožňující FAIR v Národní datové infrastruktuře ČR',
    },
    'overview.browseResources': { en: 'Browse resources', cs: 'Procházet zdroje' },
    'overview.author': { en: 'Author in FIP Wizard', cs: 'Tvořit ve FIP Wizardu' },
    'overview.authorHint': {
        en: 'FIP Wizard is the recommended tool for describing resources and authoring profiles',
        cs: 'FIP Wizard je doporučený nástroj pro popis zdrojů a tvorbu profilů',
    },
    'overview.statusesHint': {
        en: 'Not every declared resource is in production yet: profiles also record what is planned, under consideration or to be replaced.',
        cs: 'Ne každý deklarovaný zdroj je již v provozu: profily zaznamenávají i to, co je plánováno, zvažováno nebo bude nahrazeno.',
    },
    'overview.lead': {
        en: 'See which standards, services and tools Czech research communities use to make their data findable, accessible, interoperable and reusable (FAIR), and where their choices converge.',
        cs: 'Podívejte se, které standardy, služby a nástroje české výzkumné komunity používají, aby jejich data byla nalezitelná, dostupná, interoperabilní a znovupoužitelná (FAIR), a kde se jejich volby sbíhají.',
    },
    'overview.stat.fips': {
        en: 'FAIR Implementation Profiles',
        cs: 'Profily implementace FAIR',
    },
    'overview.stat.resources': {
        en: 'FAIR enabling resources',
        cs: 'Zdroje umožňující FAIR',
    },
    'overview.stat.organizations': { en: 'Organisations', cs: 'Organizace' },
    'overview.stat.declarations': { en: 'Declarations', cs: 'Deklarace' },
    'overview.coverage': {
        en: 'Coverage of the FAIR principles',
        cs: 'Pokrytí principů FAIR',
    },
    'overview.coverageHint': {
        en: 'The 15 FAIR principles fall into four groups. The stronger the colour of a tile, the more profiles declare at least one resource for that principle.',
        cs: '15 principů FAIR se dělí do čtyř skupin. Čím sytější barva dlaždice, tím více profilů pro daný princip deklaruje alespoň jeden zdroj.',
    },
    'overview.top': {
        en: 'Most shared resources',
        cs: 'Nejsdílenější zdroje',
    },
    'overview.topHint': {
        en: 'Declared in the most profiles, so natural building blocks of the national infrastructure.',
        cs: 'Deklarovány v nejvíce profilech, tedy přirození stavební kameny národní infrastruktury.',
    },
    'overview.profiles': {
        en: 'Recently updated profiles',
        cs: 'Naposledy aktualizované profily',
    },
    'overview.types': { en: 'Resources by category', cs: 'Zdroje podle kategorií' },
    'overview.statuses': { en: 'Declarations by status', cs: 'Deklarace podle stavu' },

    'resources.title': { en: 'FAIR enabling resources', cs: 'Zdroje umožňující FAIR' },
    'resources.lead': {
        en: 'Standards, services, policies and tools that help make research data FAIR. Communities declare the ones they use in their profiles.',
        cs: 'Standardy, služby, politiky a nástroje, které pomáhají naplňovat principy FAIR u výzkumných dat. Komunity ty, které používají, deklarují ve svých profilech.',
    },
    'resources.count': { en: 'resources', cs: 'zdrojů' },
    'resources.filter.type': { en: 'Category', cs: 'Kategorie' },
    'resources.filter.scope': { en: 'Scope', cs: 'Působnost' },
    'resources.filter.principle': { en: 'FAIR principle', cs: 'Princip FAIR' },
    'resources.filter.fip': { en: 'Declared in profile', cs: 'Deklarováno v profilu' },

    'resource.provider': { en: 'Provider', cs: 'Poskytovatel' },
    'resource.scope': { en: 'Scope', cs: 'Působnost' },
    'resource.category': { en: 'Category', cs: 'Kategorie' },
    'resource.principles': {
        en: 'Supports FAIR principles',
        cs: 'Podporuje principy FAIR',
    },
    'resource.declaredBy': {
        en: 'Declared in profiles',
        cs: 'Deklarováno v profilech',
    },
    'resource.tags': { en: 'Tags', cs: 'Štítky' },
    'resource.identifier': { en: 'Identifier', cs: 'Identifikátor' },
    'resource.noUsage': {
        en: 'No profile in this dataset declares this resource yet.',
        cs: 'Tento zdroj zatím nedeklaruje žádný profil v této datové sadě.',
    },
    'resource.usedBy': { en: 'profiles', cs: 'profilů' },

    'verification.title': { en: 'Verification', cs: 'Ověření' },
    'verification.by.verified': { en: 'Verified by {name}', cs: 'Ověřeno: {name}' },
    'verification.by.under-review': {
        en: 'Under review by {name}',
        cs: 'V posuzování: {name}',
    },
    'verification.by.self-declared': {
        en: 'Self-declared by {name}',
        cs: 'Vlastní prohlášení: {name}',
    },
    'verification.noEvidence': {
        en: 'No public evidence linked',
        cs: 'Bez odkazu na veřejný doklad',
    },
    'verification.record': { en: 'Verification record', cs: 'Záznam o ověření' },
    'verification.none': {
        en: 'No organisation has verified or reviewed this record.',
        cs: 'Tento záznam zatím žádná organizace neověřila ani neposoudila.',
    },
    'verification.noneShort': { en: 'Not reviewed', cs: 'Neposouzeno' },
    'verification.hint': {
        en: 'Who verified or reviewed this record, when, and with what result.',
        cs: 'Kdo záznam ověřil nebo posoudil, kdy a s jakým výsledkem.',
    },
    'verification.filter': { en: 'Verification', cs: 'Ověření' },
    'verification.verifiedOnly': { en: 'Verified only', cs: 'Pouze ověřené' },
    'verification.unverifiedOnly': { en: 'Not verified', cs: 'Neověřené' },
    'verification.stat': { en: 'Verified resources', cs: 'Ověřených zdrojů' },
    'verification.verifiers': { en: 'Verifying organisations', cs: 'Ověřující organizace' },
    'verification.verifiersHint': {
        en: 'Number of resource and profile records each organisation has verified or reviewed.',
        cs: 'Počet záznamů zdrojů a profilů, které daná organizace ověřila nebo posoudila.',
    },

    'fips.title': { en: 'FAIR Implementation Profiles', cs: 'Profily implementace FAIR' },
    'fips.lead': {
        en: 'A FAIR Implementation Profile (FIP) records which resources one research community uses to meet each FAIR principle.',
        cs: 'Profil implementace FAIR (FIP) zaznamenává, které zdroje jedna výzkumná komunita používá k naplnění jednotlivých principů FAIR.',
    },
    'fip.community': { en: 'Community', cs: 'Komunita' },
    'fip.domain': { en: 'Domain', cs: 'Doména' },
    'fip.version': { en: 'Version', cs: 'Verze' },
    'fip.updated': { en: 'Updated', cs: 'Aktualizováno' },
    'fip.declarations': { en: 'Declarations', cs: 'Deklarace' },
    'fip.principlesCovered': { en: 'Principles covered', cs: 'Pokrytých principů' },
    'fip.resourcesUsed': { en: 'Resources declared', cs: 'Deklarovaných zdrojů' },
    'fip.openProfile': { en: 'Open profile', cs: 'Otevřít profil' },
    'fip.gap': {
        en: 'No resource declared for this principle.',
        cs: 'Pro tento princip není deklarován žádný zdroj.',
    },
    'fip.status.published': { en: 'Published', cs: 'Publikováno' },
    'fip.status.draft': { en: 'Draft', cs: 'Pracovní verze' },

    'matrix.title': { en: 'FAIR Matrix', cs: 'Matice FAIR' },
    'matrix.lead': {
        en: 'The FAIR Matrix compares profiles: FAIR principles in rows, profiles in columns. In the FIP methodology it is called the FAIR convergence matrix, because it shows convergence: where several communities declare the same resource, their choices converge, and they can more easily reuse each other’s data and tools.',
        cs: 'Matice FAIR porovnává profily: v řádcích principy FAIR, ve sloupcích profily. V metodice FIP se nazývá matice konvergence FAIR, protože ukazuje konvergenci: kde více komunit deklaruje stejný zdroj, jejich volby se sbíhají a mohou snáze využívat data a nástroje ostatních.',
    },
    'matrix.select': {
        en: 'Profiles to compare',
        cs: 'Porovnávané profily',
    },
    'matrix.shared': {
        en: 'Resources shared by the selected profiles',
        cs: 'Zdroje sdílené vybranými profily',
    },
    'matrix.sharedHint': {
        en: 'Resources declared in more than one selected profile, and in how many.',
        cs: 'Zdroje deklarované ve více než jednom vybraném profilu a v kolika.',
    },
    'matrix.legend': { en: 'Status', cs: 'Stav' },

    'common.explain': {
        en: 'What does this mean?',
        cs: 'Co to znamená?',
    },
    'common.previous': {
        en: 'Previous',
        cs: 'Předchozí',
    },
    'common.next': {
        en: 'Next',
        cs: 'Další',
    },
    'common.pagination': {
        en: 'Pagination',
        cs: 'Stránkování',
    },
    'overview.newToFair': {
        en: 'New to FAIR? Start here',
        cs: 'Poprvé u FAIR? Začněte zde',
    },
    'overview.shareLegend': {
        en: 'Share of profiles',
        cs: 'Podíl profilů',
    },
    'overview.ofProfiles': {
        en: '{count} of {total} profiles declare a resource',
        cs: 'Zdroj deklaruje {count} z {total} profilů',
    },
    'overview.allProfiles': {
        en: 'All profiles',
        cs: 'Všechny profily',
    },
    'overview.state': {
        en: 'Status and verification',
        cs: 'Stav a ověření',
    },
    'overview.verifiedProfiles': {
        en: 'Verified profiles',
        cs: 'Ověřené profily',
    },
    'overview.notVerified': {
        en: 'Not verified',
        cs: 'Neověřeno',
    },
    'overview.typesHint': {
        en: 'What kinds of resources the dataset contains.',
        cs: 'Jaké druhy zdrojů datová sada obsahuje.',
    },
    'fips.searchPlaceholder': {
        en: 'Search by name, community or domain…',
        cs: 'Hledat podle názvu, komunity nebo domény…',
    },
    'fips.filter.domain': {
        en: 'Domain',
        cs: 'Doména',
    },
    'fips.filter.status': {
        en: 'Profile status',
        cs: 'Stav profilu',
    },
    'fips.sort': {
        en: 'Sort by',
        cs: 'Řadit podle',
    },
    'fips.sort.updated': {
        en: 'Recently updated',
        cs: 'Naposledy aktualizované',
    },
    'fips.sort.title': {
        en: 'Name',
        cs: 'Název',
    },
    'fips.sort.coverage': {
        en: 'Principles covered',
        cs: 'Pokryté principy',
    },
    'fips.count': {
        en: '{shown} of {total} profiles',
        cs: '{shown} z {total} profilů',
    },
    'fip.declarationsHint': {
        en: 'For each FAIR principle: the resources this community declares, and their status.',
        cs: 'Pro každý princip FAIR: zdroje, které tato komunita deklaruje, a jejich stav.',
    },
    'resource.explain': {
        en: 'What do these principles mean?',
        cs: 'Co tyto principy znamenají?',
    },
    'resource.allPrinciples': {
        en: 'All FAIR principles in the glossary',
        cs: 'Všechny principy FAIR ve slovníčku',
    },
    'matrix.howTo': {
        en: 'How to read the matrix',
        cs: 'Jak matici číst',
    },
    'matrix.howTo.cells': {
        en: 'Each tile shows how many resources a profile declares for a principle. A solid tile means the profile declares the resource most shared for that principle.',
        cs: 'Každá dlaždice ukazuje, kolik zdrojů profil pro daný princip deklaruje. Sytá dlaždice znamená, že profil deklaruje pro tento princip nejsdílenější zdroj.',
    },
    'matrix.howTo.gaps': {
        en: 'A dash is a gap: the profile declares nothing for that principle. Gaps are not necessarily failures, but they show where a community could still make a choice.',
        cs: 'Pomlčka je mezera: profil pro daný princip nic nedeklaruje. Nemusí jít o nedostatek, ale ukazuje, kde se komunita ještě může rozhodnout.',
    },
    'matrix.howTo.detail': {
        en: 'Select a row to see which resources are behind it and who declares them. Choose profiles above to compare only those you care about.',
        cs: 'Výběrem řádku zobrazíte, které zdroje za ním stojí a kdo je deklaruje. Nahoře můžete vybrat jen profily, které chcete porovnat.',
    },
    'matrix.all': {
        en: 'All',
        cs: 'Všechny',
    },
    'matrix.none': {
        en: 'None',
        cs: 'Žádný',
    },
    'matrix.filterProfiles': {
        en: 'Filter profiles…',
        cs: 'Filtrovat profily…',
    },
    'matrix.selectHint': {
        en: 'Select at least two profiles to compare them.',
        cs: 'Pro porovnání vyberte alespoň dva profily.',
    },
    'matrix.view': {
        en: 'View',
        cs: 'Zobrazení',
    },
    'matrix.view.compact': {
        en: 'Compact',
        cs: 'Kompaktní',
    },
    'matrix.view.full': {
        en: 'Full table',
        cs: 'Celá tabulka',
    },
    'matrix.principle': {
        en: 'FAIR principle',
        cs: 'Princip FAIR',
    },
    'matrix.mostShared': {
        en: 'Most shared resource',
        cs: 'Nejsdílenější zdroj',
    },
    'matrix.noneShared': {
        en: 'None shared',
        cs: 'Žádný sdílený',
    },
    'matrix.stat.profiles': {
        en: 'Profiles compared',
        cs: 'Porovnávané profily',
    },
    'matrix.stat.principles': {
        en: 'Principles with a shared resource',
        cs: 'Principy se sdíleným zdrojem',
    },
    'matrix.stat.resources': {
        en: 'Shared resources',
        cs: 'Sdílené zdroje',
    },
    'matrix.stat.gaps': {
        en: 'Gaps',
        cs: 'Mezery',
    },
    'matrix.legend.top': {
        en: 'Declares the most shared resource',
        cs: 'Deklaruje nejsdílenější zdroj',
    },
    'matrix.legend.other': {
        en: 'Declares other resources',
        cs: 'Deklaruje jiné zdroje',
    },
    'matrix.legend.gap': {
        en: 'Gap: nothing declared',
        cs: 'Mezera: nic nedeklarováno',
    },
    'matrix.resourcesFor': {
        en: 'Resources declared',
        cs: 'Deklarované zdroje',
    },
    'matrix.gapsIn': {
        en: 'Nothing declared in',
        cs: 'Nic nedeklarováno v',
    },
    'matrix.noDeclarations': {
        en: 'No selected profile declares a resource for this principle.',
        cs: 'Žádný vybraný profil pro tento princip nedeklaruje zdroj.',
    },
    'glossary.title': {
        en: 'Glossary',
        cs: 'Slovníček',
    },
    'glossary.lead': {
        en: 'The terms used in this dashboard, in plain language.',
        cs: 'Pojmy používané v tomto dashboardu, srozumitelně.',
    },
    'glossary.terms': {
        en: 'Key terms',
        cs: 'Základní pojmy',
    },
    'glossary.principles': {
        en: 'The 15 FAIR principles',
        cs: '15 principů FAIR',
    },
    'glossary.principlesHint': {
        en: 'Each principle in plain words, followed by its official wording.',
        cs: 'Každý princip srozumitelně a poté v oficiálním znění.',
    },
    'glossary.official': {
        en: 'Official wording',
        cs: 'Oficiální znění',
    },
    'glossary.findResources': {
        en: 'Resources for this principle',
        cs: 'Zdroje pro tento princip',
    },
    'glossary.categories': {
        en: 'Resource categories',
        cs: 'Kategorie zdrojů',
    },
    'glossary.values': {
        en: 'Labels you will see',
        cs: 'Označení, která uvidíte',
    },
    'glossary.fair.title': {
        en: 'FAIR',
        cs: 'FAIR',
    },
    'glossary.fair.text': {
        en: 'Short for Findable, Accessible, Interoperable and Reusable: four qualities that make research data useful to both people and computers. FAIR does not mean open: data can be FAIR and still have restricted access.',
        cs: 'Zkratka slov Findable, Accessible, Interoperable a Reusable (nalezitelná, dostupná, interoperabilní, znovupoužitelná): čtyři vlastnosti, díky nimž jsou výzkumná data užitečná lidem i počítačům. FAIR neznamená otevřená: data mohou být FAIR, a přesto mít omezený přístup.',
    },
    'glossary.principle.title': {
        en: 'FAIR principle',
        cs: 'Princip FAIR',
    },
    'glossary.principle.text': {
        en: 'One of 15 guiding principles that spell out what FAIR requires. Codes such as F1 or A1.2 start with the letter of their group.',
        cs: 'Jeden z 15 hlavních principů, které upřesňují, co FAIR vyžaduje. Kódy jako F1 nebo A1.2 začínají písmenem své skupiny.',
    },
    'glossary.fip.title': {
        en: 'FAIR Implementation Profile (FIP), profile',
        cs: 'Profil implementace FAIR (FIP), profil',
    },
    'glossary.fip.text': {
        en: 'The list of resources one research community uses to meet each FAIR principle. This dashboard calls it a profile for short.',
        cs: 'Seznam zdrojů, které jedna výzkumná komunita používá k naplnění jednotlivých principů FAIR. Tento dashboard jej zkráceně nazývá profil.',
    },
    'glossary.resource.title': {
        en: 'FAIR enabling resource, resource',
        cs: 'Zdroj umožňující FAIR, zdroj',
    },
    'glossary.resource.text': {
        en: 'Anything that helps to meet a FAIR principle, such as a standard, service, policy or tool. DOI and Zenodo are examples. This dashboard calls it a resource for short.',
        cs: 'Cokoli, co pomáhá naplnit princip FAIR, například standard, služba, politika nebo nástroj. Příkladem je DOI nebo Zenodo. Tento dashboard jej zkráceně nazývá zdroj.',
    },
    'glossary.declaration.title': {
        en: 'Declaration',
        cs: 'Deklarace',
    },
    'glossary.declaration.text': {
        en: 'One entry in a profile: this community uses this resource for this FAIR principle, with a status saying how far along it is.',
        cs: 'Jedna položka profilu: tato komunita používá tento zdroj pro tento princip FAIR, se stavem, který říká, jak daleko je.',
    },
    'glossary.community.title': {
        en: 'Community',
        cs: 'Komunita',
    },
    'glossary.community.text': {
        en: 'A group of researchers or an organisation that shares data practices, for example in one research field. Each profile belongs to one community.',
        cs: 'Skupina výzkumníků nebo organizace se společnými postupy práce s daty, například v jednom oboru. Každý profil patří jedné komunitě.',
    },
    'glossary.matrix.title': {
        en: 'FAIR Matrix (FAIR convergence matrix)',
        cs: 'Matice FAIR (matice konvergence FAIR)',
    },
    'glossary.matrix.text': {
        en: 'A table comparing profiles: FAIR principles in rows, profiles in columns, resources in the cells. It shows convergence, meaning where communities choose the same resources, and where they differ.',
        cs: 'Tabulka porovnávající profily: v řádcích principy FAIR, ve sloupcích profily, v buňkách zdroje. Ukazuje konvergenci, tedy kde si komunity volí stejné zdroje a kde se liší.',
    },
    'glossary.metadata.title': {
        en: 'Metadata',
        cs: 'Metadata',
    },
    'glossary.metadata.text': {
        en: 'Data about data: the description of a dataset, such as its title, authors, date and licence. “(Meta)data” in the principles means both the data and their description.',
        cs: 'Data o datech: popis datové sady, například název, autoři, datum a licence. „(Meta)data“ v principech znamená data i jejich popis.',
    },
    'glossary.pid.title': {
        en: 'Persistent identifier (PID)',
        cs: 'Trvalý identifikátor (PID)',
    },
    'glossary.pid.text': {
        en: 'A permanent, globally unique reference to something, such as a DOI for a dataset or an ORCID for a person. Unlike an ordinary web address, it is maintained so that it keeps working.',
        cs: 'Trvalý a globálně jedinečný odkaz na něco, například DOI pro datovou sadu nebo ORCID pro osobu. Na rozdíl od běžné webové adresy je udržován tak, aby stále fungoval.',
    },
    'glossary.status.title': {
        en: 'Declaration status',
        cs: 'Stav deklarace',
    },
    'glossary.status.text': {
        en: 'How far along a community is with a declared resource.',
        cs: 'Jak daleko je komunita s deklarovaným zdrojem.',
    },
    'glossary.status.in-use': {
        en: 'The community uses this resource today.',
        cs: 'Komunita tento zdroj v současnosti používá.',
    },
    'glossary.status.planned': {
        en: 'The community has decided to adopt this resource.',
        cs: 'Komunita se rozhodla tento zdroj zavést.',
    },
    'glossary.status.under-consideration': {
        en: 'The community is evaluating this resource but has not decided.',
        cs: 'Komunita tento zdroj posuzuje, ale ještě se nerozhodla.',
    },
    'glossary.status.to-be-replaced': {
        en: 'The community uses this resource but intends to move away from it.',
        cs: 'Komunita tento zdroj používá, ale chce od něj přejít jinam.',
    },
    'glossary.scope.title': {
        en: 'Scope',
        cs: 'Působnost',
    },
    'glossary.scope.text': {
        en: 'How widely a resource or organisation operates.',
        cs: 'Jak široce zdroj nebo organizace působí.',
    },
    'glossary.scope.global': {
        en: 'Used worldwide.',
        cs: 'Používá se celosvětově.',
    },
    'glossary.scope.european': {
        en: 'Operated at the European level.',
        cs: 'Provozováno na evropské úrovni.',
    },
    'glossary.scope.national': {
        en: 'Operated for the Czech Republic.',
        cs: 'Provozováno pro Českou republiku.',
    },
    'glossary.scope.institutional': {
        en: 'Operated by a single institution.',
        cs: 'Provozováno jednou institucí.',
    },
    'glossary.category.title': {
        en: 'Resource category',
        cs: 'Kategorie zdroje',
    },
    'glossary.category.text': {
        en: 'The kind of thing a resource is, for example a metadata schema or a repository.',
        cs: 'Druh zdroje, například metadatové schéma nebo repozitář.',
    },
    'glossary.verification.title': {
        en: 'Verification',
        cs: 'Ověření',
    },
    'glossary.verification.text': {
        en: 'Whether an organisation has checked a resource or a profile record. Only the Verified level counts as verified.',
        cs: 'Zda organizace zkontrolovala záznam zdroje nebo profilu. Za ověřený se považuje jen záznam s úrovní Ověřeno.',
    },
    'glossary.verification.verified': {
        en: 'An independent organisation has checked the record and confirmed it.',
        cs: 'Nezávislá organizace záznam zkontrolovala a potvrdila.',
    },
    'glossary.verification.under-review': {
        en: 'An organisation is checking the record; there is no result yet.',
        cs: 'Organizace záznam kontroluje; výsledek zatím není k dispozici.',
    },
    'glossary.verification.self-declared': {
        en: 'The owner of the record vouches for it; nobody else has checked it.',
        cs: 'Za záznam ručí jeho vlastník; nikdo jiný jej nezkontroloval.',
    },
    'glossary.group.F': {
        en: 'Data and their descriptions can be found by people and computers.',
        cs: 'Data a jejich popis mohou najít lidé i počítače.',
    },
    'glossary.group.A': {
        en: 'Once found, it is clear how to get the data, including any access conditions.',
        cs: 'Po nalezení je jasné, jak data získat, včetně případných podmínek přístupu.',
    },
    'glossary.group.I': {
        en: 'Data can be combined with other data and processed by other tools.',
        cs: 'Data lze kombinovat s jinými daty a zpracovávat dalšími nástroji.',
    },
    'glossary.group.R': {
        en: 'Data are described well enough to be reused for new purposes.',
        cs: 'Data jsou popsána dost dobře, aby je šlo znovu použít k novým účelům.',
    },

    'about.title': { en: 'About this dashboard', cs: 'O tomto dashboardu' },
    'about.whatTitle': { en: 'What is a FIP?', cs: 'Co je FIP?' },
    'about.what': {
        en: 'A FAIR Implementation Profile (FIP) lists the FAIR enabling resources a research community uses to meet each of the FAIR principles. Communities that publish their profiles can reuse each other’s choices instead of solving the same problems again.',
        cs: 'Profil implementace FAIR (FIP) uvádí zdroje umožňující FAIR, které výzkumná komunita používá k naplnění jednotlivých principů FAIR. Komunity, které své profily zveřejní, mohou volby přebírat od sebe navzájem místo opakovaného řešení stejných problémů.',
    },
    'about.authoringTitle': {
        en: 'Authoring resources and profiles',
        cs: 'Tvorba zdrojů a profilů',
    },
    'about.authoring': {
        en: 'This dashboard only presents FAIR enabling resources and FAIR Implementation Profiles. To describe a resource or to author your community’s profile, we recommend the FIP Wizard.',
        cs: 'Tento dashboard zdroje umožňující FAIR a profily implementace FAIR pouze zobrazuje. K popisu zdroje nebo k sestavení profilu vaší komunity doporučujeme FIP Wizard.',
    },
    'about.authoringCta': { en: 'Open FIP Wizard', cs: 'Otevřít FIP Wizard' },
    'about.dataTitle': { en: 'About the data', cs: 'O datech' },
    'about.apiTitle': { en: 'API', cs: 'API' },
    'about.api': {
        en: 'Everything shown here comes from the dashboard’s own JSON API. The interactive documentation is generated by FastAPI.',
        cs: 'Vše zde zobrazené pochází z vlastního JSON API dashboardu. Interaktivní dokumentaci generuje FastAPI.',
    },
    'about.sources': { en: 'Reference links', cs: 'Referenční odkazy' },
    'about.backend': { en: 'Backend status', cs: 'Stav backendu' },
}

const groupNames: Record<PrincipleGroup, LocalizedText> = {
    F: { en: 'Findable', cs: 'Nalezitelná' },
    A: { en: 'Accessible', cs: 'Dostupná' },
    I: { en: 'Interoperable', cs: 'Interoperabilní' },
    R: { en: 'Reusable', cs: 'Znovupoužitelná' },
}

const statusNames: Record<DeclarationStatus, LocalizedText> = {
    'in-use': { en: 'In use', cs: 'Používá se' },
    planned: { en: 'Planned', cs: 'Plánováno' },
    'under-consideration': { en: 'Under consideration', cs: 'Zvažuje se' },
    'to-be-replaced': { en: 'To be replaced', cs: 'Bude nahrazeno' },
}

const verificationNames: Record<VerificationLevel, LocalizedText> = {
    verified: { en: 'Verified', cs: 'Ověřeno' },
    'self-declared': { en: 'Self-declared', cs: 'Vlastní prohlášení' },
    'under-review': { en: 'Under review', cs: 'V posuzování' },
}

const scopeNames: Record<Scope, LocalizedText> = {
    global: { en: 'Global', cs: 'Globální' },
    european: { en: 'European', cs: 'Evropský' },
    national: { en: 'National', cs: 'Národní' },
    institutional: { en: 'Institutional', cs: 'Institucionální' },
}

type I18nValue = {
    language: Language
    setLanguage: (language: Language) => void
    t: (key: string) => string
    localized: (text: LocalizedText | null | undefined) => string
    groupName: (group: PrincipleGroup) => string
    statusName: (status: DeclarationStatus) => string
    verificationName: (level: VerificationLevel) => string
    scopeName: (scope: Scope) => string
    formatDate: (value: string) => string
}

const I18nContext = createContext<I18nValue | null>(null)

function initialLanguage(): Language {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        if (stored === 'cs' || stored === 'en') {
            return stored
        }
    } catch {
        // Storage is unavailable (private mode); fall through to the default.
    }
    return navigator.language.startsWith('cs') ? 'cs' : 'en'
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [language, setLanguageState] = useState<Language>(initialLanguage)

    const setLanguage = useCallback((next: Language) => {
        setLanguageState(next)
        document.documentElement.lang = next
        try {
            window.localStorage.setItem(STORAGE_KEY, next)
        } catch {
            // Remembering the choice is optional.
        }
    }, [])

    const value = useMemo<I18nValue>(() => {
        const pick = (text: LocalizedText | null | undefined) =>
            text ? (language === 'cs' ? text.cs : text.en) : ''
        return {
            language,
            setLanguage,
            t: (key: string) => pick(strings[key]) || key,
            localized: pick,
            groupName: (group: PrincipleGroup) => pick(groupNames[group]),
            statusName: (status: DeclarationStatus) => pick(statusNames[status]),
            verificationName: (level: VerificationLevel) => pick(verificationNames[level]),
            scopeName: (scope: Scope) => pick(scopeNames[scope]),
            formatDate: (value: string) =>
                new Date(value).toLocaleDateString(language === 'cs' ? 'cs-CZ' : 'en-GB', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                }),
        }
    }, [language, setLanguage])

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
    const value = useContext(I18nContext)
    if (!value) {
        throw new Error('useI18n must be used inside an I18nProvider')
    }
    return value
}
